import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { CarouselPumoModuleFragment } from '../../generated/types';
import { hrefFor } from '../../routers';
import { useModuleTracking } from '../../hooks/useModuleTracking';
import {
  CarouselPumoModuleSlider,
  CAROUSEL_PUMO_MODULE_SLIDER_FRAGMENT
} from '../CarouselPumoModuleSlider';
import {
  CarouselPumoModuleGrid,
  CAROUSEL_PUMO_MODULE_GRID_FRAGMENT
} from '../CarouselPumoModuleGrid';
import { CarouselPumoModuleCategories } from '../CarouselPumoModuleCategories';
import { RestrictPlatform } from '../RestrictPlatform';
import { CAROUSEL_DEFAULT_CONFIG, CAROUSEL_NARROW_CONFIG } from '../Swiper';
import { PUMO_MODULE_SLIDE_FRAGMENT, PumoModuleSlide } from '../PumoModuleSlide';
import { NormalizePumoStyles } from '../NormalizePumoStyles';
import { EDITORIAL_BG_COLORS, EditorialBgColor } from '../PumoModuleEditorialText';

import './CarouselPumoModule.scss';

enum ImageEditorialPosition {
  Top,
  Right,
  Bottom,
  Left
}

const DEFAULT_IMAGE_EDITORIAL_POSITION = ImageEditorialPosition.Left;

export const CAROUSEL_PUMO_MODULE_FRAGMENT = gql`
  fragment CarouselPumoModuleFragment on CarouselPumoModule {
    name
    id
    layoutType
    modelId
    platform
    imageEditorialPosition
    bgColor
    mainEditorial {
      ...PumoModuleSlideFragment
    }
    ...CarouselPumoModuleSliderFragment
    ...CarouselPumoModuleGridFragment
  }
  ${CAROUSEL_PUMO_MODULE_SLIDER_FRAGMENT}
  ${CAROUSEL_PUMO_MODULE_GRID_FRAGMENT}
  ${PUMO_MODULE_SLIDE_FRAGMENT}
`;

enum LayoutType {
  Carousel = 1,
  Grid = 2
}

const useCarouselModuleType = (carouselPumoModule: CarouselPumoModuleFragment) => {
  const { pathname } = useLocation();

  /**
   * This is a temporary workaround for getting the category tiles styled correctly on certain pages.
   * TODO: Implement proper support for this in our CMS.
   */
  if (
    (pathname === hrefFor.BeautyLandingPage() ||
      pathname === hrefFor.HomeLandingPage() ||
      pathname === hrefFor.EditorialPage({ slug: 'the-holiday-guide' })) &&
    carouselPumoModule.name === 'Categories'
  )
    return 'categories';

  if (carouselPumoModule.layoutType === LayoutType.Grid) return 'grid';

  return 'carousel';
};

interface Props {
  carouselPumoModule: CarouselPumoModuleFragment;
}

export const CarouselPumoModule: React.FC<Props> = ({ carouselPumoModule }) => {
  const type = useCarouselModuleType(carouselPumoModule);

  const { ref, onClick } = useModuleTracking({
    name: carouselPumoModule.name,
    attachClickHandler: false,
    variants: carouselPumoModule.cells
  });

  const hasMainEditorial =
    Boolean(carouselPumoModule.mainEditorial?.featureImage?.imageSrc) ||
    Boolean(carouselPumoModule.mainEditorial?.slideText?.taglineText?.body);

  const backgroundColor = EDITORIAL_BG_COLORS[carouselPumoModule.bgColor as EditorialBgColor];

  const imageEditorialPosition: undefined | ImageEditorialPosition = (() => {
    if (typeof carouselPumoModule.imageEditorialPosition !== 'number') {
      return DEFAULT_IMAGE_EDITORIAL_POSITION;
    }

    return ImageEditorialPosition[
      ImageEditorialPosition[
        carouselPumoModule.imageEditorialPosition
      ] as keyof typeof ImageEditorialPosition
    ];
  })();

  const isVertical = useMemo(
    () =>
      [ImageEditorialPosition.Top, ImageEditorialPosition.Bottom].includes(imageEditorialPosition),
    [imageEditorialPosition]
  );

  const isHorizontal = !isVertical;

  const editorialElement = useMemo(() => {
    if (!carouselPumoModule.mainEditorial) return null;

    if (!hasMainEditorial) return null;

    return (
      <NormalizePumoStyles
        className={classNames('CarouselPumoModule__editorial', {
          'CarouselPumoModule__main-editorial--vertical': isVertical
        })}
      >
        <PumoModuleSlide slide={carouselPumoModule.mainEditorial} />
      </NormalizePumoStyles>
    );
  }, [carouselPumoModule.mainEditorial, hasMainEditorial, isVertical]);

  const contentElement = useMemo(
    () => (
      <div
        className={classNames('CarouselPumoModule__content', {
          'CarouselPumoModule__content--vertical': hasMainEditorial && isVertical,
          'CarouselPumoModule__content--horizontal': hasMainEditorial && isHorizontal
        })}
      >
        {type === 'categories' && (
          <CarouselPumoModuleCategories carouselPumoModule={carouselPumoModule} onClick={onClick} />
        )}
        {type === 'grid' && (
          <CarouselPumoModuleGrid carouselPumoModuleGrid={carouselPumoModule} onClick={onClick} />
        )}
        {type === 'carousel' && (
          <CarouselPumoModuleSlider
            carouselPumoModuleSlider={carouselPumoModule}
            moduleName={carouselPumoModule.name}
            config={
              hasMainEditorial && !isVertical ? CAROUSEL_NARROW_CONFIG : CAROUSEL_DEFAULT_CONFIG
            }
            onClick={onClick}
          />
        )}
      </div>
    ),
    [carouselPumoModule, hasMainEditorial, isHorizontal, isVertical, onClick, type]
  );

  return (
    <RestrictPlatform platforms={carouselPumoModule.platform}>
      <div
        ref={ref}
        className={classNames('CarouselPumoModule', {
          'CarouselPumoModule--editorial': hasMainEditorial,
          'CarouselPumoModule__editorial--horizontal': hasMainEditorial && isHorizontal,
          'CarouselPumoModule__editorial--vertical': hasMainEditorial && isVertical
        })}
        data-model-id={carouselPumoModule.modelId}
        style={{ ...(backgroundColor ? { backgroundColor: backgroundColor } : {}) }}
      >
        {[ImageEditorialPosition.Left, ImageEditorialPosition.Top].includes(
          imageEditorialPosition
        ) && (
          <>
            {editorialElement}

            {contentElement}
          </>
        )}

        {[ImageEditorialPosition.Right, ImageEditorialPosition.Bottom].includes(
          imageEditorialPosition
        ) && (
          <>
            {contentElement}

            {editorialElement}
          </>
        )}
      </div>
    </RestrictPlatform>
  );
};
